import {
    type ReactElement,
    isValidElement,
    type ReactNode,
    Children
} from "react";
import type IResizablePanelProps from "./components/panel/type";
import type {
    ResizableInitialSizesConstantType,
    ResizableReleaseConstantType,
    ResizableBoundsConstantType,
    ResizableLayoutConstantType,
    ResizablePanelConfigType,
    ResizableParsedChildren,
    ResizableReleaseType,
    ResizableBoundsType,
    ResizablePart,
    ResizableSize
} from "./type";

export const RESIZABLE_DEFAULT_MIN = 10;

export const RESIZABLE_DEFAULT_MAX = 100;

export const RESIZABLE_PERCENT_TOTAL = 100;

export const RESIZABLE_KEYBOARD_STEP = 5;

export const RESIZABLE_SHIFT_MULTIPLIER = 2;

export const RESIZABLE_EPSILON = 0.5;

export const RESIZABLE_PERCENT_PRECISION = 100;

export const RESIZABLE_STORAGE_PREFIX = "ncore-ui-kit:resizable:";

export const getResizablePart = (type: unknown): ResizablePart | undefined => {
    if(typeof type !== "function" && (typeof type !== "object" || type === null)) {
        return undefined;
    }

    if(!("resizablePart" in type)) {
        return undefined;
    }

    return type.resizablePart === "panel" || type.resizablePart === "handle" ? type.resizablePart : undefined;
};

const isPanelElement = (element: ReactElement): element is ReactElement<IResizablePanelProps> => {
    return getResizablePart(element.type) === "panel";
};

export const parseResizableChildren = (children: ReactNode): ResizableParsedChildren => {
    const parsed: ResizableParsedChildren = {
        entries: [],
        panels: []
    };

    Children.toArray(children).forEach((child) => {
        if(!isValidElement(child)) {
            return;
        }

        const part = getResizablePart(child.type);

        if(part === "panel" && isPanelElement(child)) {
            parsed.entries.push({
                index: parsed.panels.length,
                element: child,
                kind: "panel"
            });

            parsed.panels.push(child);

            return;
        }

        if(part === "handle") {
            parsed.entries.push({
                index: parsed.panels.length - 1,
                element: child,
                kind: "handle"
            });
        }
    });

    return parsed;
};

export const RESIZABLE_PANEL_CONFIG_KEYS = [
    "onCollapsedChange",
    "isDefaultCollapsed",
    "collapsedSize",
    "isCollapsible",
    "defaultSize",
    "isCollapsed",
    "maxSize",
    "minSize",
    "title",
    "icon"
] as const;

export const getPanelViewProps = (props: IResizablePanelProps): Omit<IResizablePanelProps, typeof RESIZABLE_PANEL_CONFIG_KEYS[number]> => {
    const viewProps = {
        ...props
    };

    RESIZABLE_PANEL_CONFIG_KEYS.forEach((key) => {
        Reflect.deleteProperty(viewProps, key);
    });

    return viewProps;
};

export const clamp = (value: number, low: number, high: number): number => {
    return Math.min(Math.max(value, low), high);
};

export const sum = (values: Array<number>): number => {
    return values.reduce((total, value) => total + value, 0);
};

export const getSizePx = ({
    available,
    size
}: {
    size: ResizableSize;
    available: number;
}): number => {
    if(typeof size === "number") {
        return size * available / RESIZABLE_PERCENT_TOTAL;
    }

    const pixels = parseFloat(size);

    return Number.isFinite(pixels) ? pixels : 0;
};

export const getPanelConfig = ({
    collapsedFallback,
    collapsedSize,
    isCollapsible,
    defaultSize,
    available,
    maxSize,
    minSize
}: {
    collapsedSize?: ResizableSize;
    defaultSize?: ResizableSize;
    collapsedFallback: number;
    isCollapsible?: boolean;
    maxSize?: ResizableSize;
    minSize?: ResizableSize;
    available: number;
}): ResizablePanelConfigType => {
    const minPx = getSizePx({
        size: minSize ?? RESIZABLE_DEFAULT_MIN,
        available
    });

    const maxPx = getSizePx({
        size: maxSize ?? RESIZABLE_DEFAULT_MAX,
        available
    });

    return {
        collapsedPx: collapsedSize === undefined ? collapsedFallback : getSizePx({
            size: collapsedSize,
            available
        }),
        defaultPx: defaultSize === undefined ? undefined : getSizePx({
            size: defaultSize,
            available
        }),
        isFixed: typeof defaultSize === "string",
        isCollapsible: !!isCollapsible,
        maxPx: Math.max(minPx, maxPx),
        minPx
    };
};

export const getPercents = (sizes: Array<number>, available: number): Array<number> => {
    if(available <= 0) {
        return sizes.map(() => 0);
    }

    return sizes.map((size) => Math.round(size / available * RESIZABLE_PERCENT_TOTAL * RESIZABLE_PERCENT_PRECISION) / RESIZABLE_PERCENT_PRECISION);
};

export const resolveLayout = ({
    pinned = [],
    available,
    collapsed,
    configs,
    sizes
}: ResizableLayoutConstantType): Array<number> => {
    const next = sizes.map((size, index) => {
        const config = configs[index];

        if(!config) {
            return size;
        }

        if(collapsed[index]) {
            return config.collapsedPx;
        }

        return clamp(size, config.minPx, config.maxPx);
    });

    for(let pass = 0; pass <= configs.length; pass += 1) {
        const diff = available - sum(next);

        if(Math.abs(diff) < RESIZABLE_EPSILON) {
            break;
        }

        const rooms = next.map((size, index) => {
            const config = configs[index];

            if(!config || collapsed[index]) {
                return 0;
            }

            return diff > 0 ? config.maxPx - size : size - config.minPx;
        });

        const groups: Array<Array<number>> = [
            next.map((_, index) => index).filter((index) => !configs[index]?.isFixed && !pinned[index]),
            next.map((_, index) => index).filter((index) => !pinned[index]),
            next.map((_, index) => index)
        ];

        const group = groups.find((indexes) => sum(indexes.map((index) => rooms[index] ?? 0)) > RESIZABLE_EPSILON);

        if(!group) {
            break;
        }

        const weights = group.map((index) => next[index] ?? 0);

        const weightTotal = sum(weights) || group.length;

        group.forEach((index, order) => {
            const weight = sum(weights) ? weights[order] ?? 0 : 1;

            const share = Math.abs(diff) * weight / weightTotal;

            const amount = Math.min(rooms[index] ?? 0, share);

            next[index] = (next[index] ?? 0) + (diff > 0 ? amount : -amount);
        });
    }

    return next;
};

export const getInitialSizes = ({
    available,
    collapsed,
    configs
}: ResizableInitialSizesConstantType): Array<number> => {
    const known = configs.map((config, index) => {
        if(collapsed[index]) {
            return config.collapsedPx;
        }

        return config.defaultPx;
    });

    const knownTotal = sum(known.map((size) => size ?? 0));

    const unknownCount = known.filter((size) => size === undefined).length;

    const share = unknownCount ? Math.max(0, available - knownTotal) / unknownCount : 0;

    return resolveLayout({
        sizes: known.map((size) => size ?? share),
        available,
        collapsed,
        configs
    });
};

export const getBounds = ({
    isIncludeCollapse = true,
    configs,
    index,
    sizes
}: ResizableBoundsConstantType): ResizableBoundsType => {
    const prev = configs[index];

    const next = configs[index + 1];

    const current = sizes[index] ?? 0;

    const total = current + (sizes[index + 1] ?? 0);

    if(!prev || !next) {
        return {
            high: current,
            low: current,
            nextLow: 0,
            prevLow: 0,
            total
        };
    }

    const prevLow = prev.isCollapsible && isIncludeCollapse ? prev.collapsedPx : prev.minPx;

    const nextLow = next.isCollapsible && isIncludeCollapse ? next.collapsedPx : next.minPx;

    const low = Math.max(prevLow, total - next.maxPx);

    const high = Math.min(prev.maxPx, total - nextLow);

    if(low > high) {
        return {
            high: current,
            low: current,
            nextLow,
            prevLow,
            total
        };
    }

    return {
        nextLow,
        prevLow,
        total,
        high,
        low
    };
};

export const resolveRelease = ({
    configs,
    desired,
    sizes,
    index
}: ResizableReleaseConstantType): ResizableReleaseType => {
    const prev = configs[index];

    const next = configs[index + 1];

    const bounds = getBounds({
        configs,
        sizes,
        index
    });

    const collapsed: Array<boolean | undefined> = sizes.map(() => undefined);

    const nextSizes = [...sizes];

    if(!prev || !next) {
        return {
            sizes: nextSizes,
            collapsed
        };
    }

    let prevSize = clamp(desired, bounds.low, bounds.high);

    if(prev.isCollapsible) {
        const isBelow = prevSize < prev.minPx;

        const isCollapse = isBelow && prevSize < (prev.collapsedPx + prev.minPx) / 2;

        collapsed[index] = isCollapse;

        if(isBelow) {
            prevSize = isCollapse ? prev.collapsedPx : prev.minPx;
        }
    }

    let nextSize = bounds.total - prevSize;

    if(next.isCollapsible) {
        const isBelow = nextSize < next.minPx;

        const isCollapse = isBelow && nextSize < (next.collapsedPx + next.minPx) / 2;

        collapsed[index + 1] = isCollapse;

        if(isBelow) {
            nextSize = isCollapse ? next.collapsedPx : next.minPx;

            prevSize = bounds.total - nextSize;
        }
    }

    nextSizes[index] = prevSize;

    nextSizes[index + 1] = nextSize;

    return {
        sizes: nextSizes,
        collapsed
    };
};
